import React from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { AUTH_STEPPER_CARD_CLASS } from "./authStyles";

/** One entry in a signup flow's step indicator. */
export interface AuthStep {
  path: string;
  label: string;
}

/**
 * Index of the most specific step matching `pathname`, or -1 outside the flow.
 * Keeps the LAST match so nested paths like "/signup/verify" resolve to their
 * own step instead of the "/signup" parent.
 */
export const getActiveStepIndex = (pathname: string, steps: AuthStep[]) =>
  steps.reduce(
    (acc, step, index) =>
      pathname === step.path || pathname.startsWith(`${step.path}/`) ? index : acc,
    -1,
  );

interface StepProgressProps {
  steps: AuthStep[];
  /** Output of `getActiveStepIndex` for the current location. */
  currentIndex: number;
}

/**
 * Bordered card holding a signup flow's numbered steps, shared by the student
 * (`SignupLayout`) and business (`BusinessSignupLayout`) flows so the two
 * cannot drift apart. `mb-6` keeps it clear of the step card below.
 */
const StepProgress: React.FC<StepProgressProps> = ({ steps, currentIndex }) => {
  const { t } = useTranslation();

  return (
    <div className={`${AUTH_STEPPER_CARD_CLASS} mb-6`}>
      <div className="flex items-center">
        {steps.map((step, index) => {
          const isActive = index === currentIndex;
          const isDone = index < currentIndex;
          return (
            <React.Fragment key={step.path}>
              {index > 0 && (
                <div
                  className={`h-0.5 flex-1 rounded-full ${
                    isDone ? "bg-[#ffa217]" : "bg-gray-200"
                  }`}
                />
              )}
              <div className="flex flex-col items-center px-1">
                <span
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition ${
                    isActive
                      ? "bg-[#ffa217] text-white ring-4 ring-[#ffa217]/15"
                      : isDone
                        ? "bg-[#ffa217] text-white"
                        : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {isDone ? <Check size={14} strokeWidth={3} /> : index + 1}
                </span>
                {/* The branding panel halves this column at `lg`, where the six
                    labels no longer fit — circles only until `xl` gives them
                    room again. */}
                <span
                  className={`mt-1.5 hidden text-xs transition sm:block lg:hidden xl:block ${
                    isActive
                      ? "font-medium text-[#ffa217]"
                      : isDone
                        ? "font-medium text-gray-600"
                        : "text-gray-400"
                  }`}
                >
                  {t(step.label)}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default StepProgress;
